import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useParams } from '@tanstack/react-router'
import { useMemo } from 'react'

import type { TrackedOrder } from '@/bindings/TrackedOrder'
import { Page } from '@/components/layout'
import { sendOrderFeedback } from '@/features/feedback/api'
import { OrderFeedbackPanel } from '@/features/feedback/form'
import { myOrdersKeys, orderQuery, orderingKeys, sessionTrackingPath } from '@/features/ordering/api'
import { useCustomerAuth } from '@/features/ordering/customer-auth'
import { CustomerHeader, CustomerLoadError, CustomerLoading, CustomerShell } from '@/features/ordering/customer-chrome'
import { customerCancelable, ownsOrder } from '@/features/ordering/order-rules'
import { CancelOrderButton, LiveIndicator, OrderCard, StatusSteps } from '@/features/ordering/order-view'
import { useT } from '@/lib/i18n/translate'
import { branchEventsUrl, useBranchEvents } from '@/lib/live'
import { myOrderCodes } from '@/lib/my-orders'
import { loginPathFor, loginSearchFor } from '@/lib/return-path'

const ORDER_ROUTE = '/$restaurantSlug/$branchSlug/o/$code'

/**
 * `/<r>/<b>/o/<code>` — the order tracking page (PRD, G7): status, the
 * expiry countdown, the lines, cancel from SENT/CONFIRMED, and for dine-in a
 * link to the table's session. Live through G5's hints, polling behind them.
 */
export function OrderTrackingPage() {
  const { restaurantSlug, branchSlug, code } = useParams({ from: ORDER_ROUTE })
  const t = useT()
  const location = useLocation()
  const auth = useCustomerAuth()
  const queryClient = useQueryClient()
  const query = useQuery(orderQuery(restaurantSlug, branchSlug, code))
  const live = useBranchEvents(branchEventsUrl(restaurantSlug, branchSlug), () => {
    void queryClient.invalidateQueries({
      queryKey: orderingKeys.order(restaurantSlug, branchSlug, code),
    })
  })
  // The URL is the proof for an anonymous order (T6); the stored list adds nothing here.
  const codesHeld = useMemo(() => new Set([code, ...myOrderCodes(restaurantSlug, branchSlug)]), [code, restaurantSlug, branchSlug])

  if (query.isPending) return <CustomerLoading />
  // A refetch that fails keeps the last answer on screen; only a first load with nothing to show errors.
  if (!query.data) return <CustomerLoadError error={query.error} notFound={t('track.notFound')} retry={() => void query.refetch()} />

  const { restaurant, branch, order, feedback } = query.data
  const cancelable = customerCancelable(order.status)
  // Another account's order, seen logged out: the owner may just need to log back in.
  const loginToCancel = cancelable && order.ownership === 'other' && auth.status === 'anonymous'

  return (
    <CustomerShell color={restaurant.color}>
      <CustomerHeader restaurant={restaurant.name} branch={branch.name} auth={auth} />
      <Page width="sm" className="flex flex-1 flex-col gap-4">
        <OrderCard
          order={order}
          currency={branch.currency}
          main={restaurant.main_language}
          actions={
            <>
              {ownsOrder(order, codesHeld) && <CancelOrderButton restaurantSlug={restaurantSlug} branchSlug={branchSlug} order={order} />}
              {loginToCancel && (
                <Link
                  to={loginPathFor('restaurant')}
                  search={loginSearchFor('restaurant', location)}
                  className="text-accent-ink self-end text-sm font-medium hover:underline"
                >
                  {t('track.loginToCancel')}
                </Link>
              )}
            </>
          }
        >
          <StatusSteps status={order.status} />
          <p className="text-sm">{t(`track.statusHint.${order.status}`)}</p>
        </OrderCard>
        <LiveIndicator status={live} />

        {ownsOrder(order, codesHeld) && (
          <OrderFeedbackPanel
            sent={feedback}
            hint={t('feedback.order.hint', { restaurant: restaurant.name })}
            send={(message) => sendOrderFeedback(restaurantSlug, branchSlug, code, { message })}
            onSent={(sent) => {
              queryClient.setQueryData<TrackedOrder>(orderingKeys.order(restaurantSlug, branchSlug, code), (old) => (old ? { ...old, feedback: sent } : old))
              // The my-orders row would otherwise offer the form again for up to its staleTime.
              void queryClient.invalidateQueries({ queryKey: myOrdersKeys.all })
            }}
          />
        )}

        {order.session && (
          <Link
            to={sessionTrackingPath(restaurantSlug, branchSlug, order.session.code)}
            className="text-accent-ink hover:bg-accent-soft min-h-tap flex items-center justify-center rounded-lg border border-current px-4 font-medium"
          >
            {t('track.seeSession', { name: order.session.table_name })}
          </Link>
        )}
      </Page>
    </CustomerShell>
  )
}
